import { useState } from "react";
import { addDoc, collection, serverTimestamp, query, where, getDocs, updateDoc, doc, DocumentData } from "firebase/firestore";
import { db } from "../firebase";
import type { FlexFormFirestoreSelectOptionSet } from "@captiva/shared-types";

export interface CreateFlexOptionsSetParams {
  assetType: string;
  assetList: Record<string, any>[];
  fieldMappings: {
    label: string;
    value: string;
    metadata?: Record<string, string>;
  };
  companyId: string;
  collectionName?: string;
  metadataPrefix?: string;
}

export type FlexOptionsSetNotification =
  | { type: "success"; message: string }
  | { type: "error"; message: string }
  | { type: "choice"; message: string; choices: string[] }
  | null;

export interface ExistingDoc extends FlexFormFirestoreSelectOptionSet {
  linkedAssetType?: string;
  createdAt?: any;
  updatedAt?: any;
}

export interface UseCreateFlexOptionsSetResult {
  loading: boolean;
  notification: FlexOptionsSetNotification;
  clearNotification: () => void;
  actionRequired: boolean;
  existingDocs: ExistingDoc[];
  mergeOptions: () => Promise<void>;
  createNewSet: () => Promise<void>;
}

export function useCreateFlexOptionsSet(): [
  (params: CreateFlexOptionsSetParams) => Promise<void>,
  UseCreateFlexOptionsSetResult
] {
    const [loading, setLoading] = useState(false);
    const [notification, setNotification] = useState<FlexOptionsSetNotification>(null);
    const [existingDocs, setExistingDocs] = useState<ExistingDoc[]>([]);
    const [actionRequired, setActionRequired] = useState(false);
    const [pendingParams, setPendingParams] = useState<CreateFlexOptionsSetParams | null>(null);

    const createFlexOptionsSet = async ({
        assetType,
        assetList,
        fieldMappings,
        companyId,
        collectionName = "flexFormSelectOptions",
        metadataPrefix = "item"
    }: CreateFlexOptionsSetParams) => {
        setLoading(true);
        setNotification(null);
        try {
            // Check for existing flex options set
            const q = query(collection(db, collectionName), where("linkedAssetType", "==", assetType), where("companyId", "==", companyId));
            const querySnapshot = await getDocs(q);
            if (!querySnapshot.empty) {
                const docs = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as unknown as ExistingDoc));
                setExistingDocs(docs);
                setPendingParams({ assetType, assetList, fieldMappings, companyId, collectionName, metadataPrefix });
                setActionRequired(true);
                setNotification({ type: "choice", message: `Flex options sets for ${assetType} already exist. Choose:`, choices: ["merge", "createNew"] });
                setLoading(false);
                return;
            }

            const options = assetList.map((asset) => {
                const metadata: Record<string, { label: string; value: any }> = {};
                if (fieldMappings.metadata) {
                    Object.entries(fieldMappings.metadata).forEach(([metaKey, metaConfig]) => {
                        metadata[metaKey] = {
                            label: `${metadataPrefix}-${metaKey}`,
                            value: asset[metaConfig] || "",
                        };
                    });
                }
                return {
                    label: (asset[fieldMappings.label] as string) || "",
                    value: (asset[fieldMappings.value] as string) || "",
                    metadata,
                };
            }).sort((a, b) => a.label.localeCompare(b.label));

            const docData = {
                name: assetType,
                createdAt: serverTimestamp(),
                options,
                updatedAt: serverTimestamp(),
                companyId,
                linkedAssetType: assetType,
            };
            await addDoc(collection(db, collectionName), docData);
            setNotification({ type: "success", message: `Flex Options set for ${assetType} created!` });
            setTimeout(() => setNotification(null), 3000);
        } catch (err: any) {
            setNotification({ type: "error", message: err.message || "Failed to create options set." });
            setTimeout(() => setNotification(null), 3000);
        } finally {
            setLoading(false);
        }
    };

    const mergeOptions = async () => {
        if (!pendingParams) return;
        setLoading(true);
        setNotification(null);
        try {
            const params = pendingParams;
            const collectionName = params.collectionName || "flexFormSelectOptions";
            const metadataPrefix = params.metadataPrefix || "item";

            // Generate new options
            const newOptions = params.assetList.map((asset) => {
                const metadata: Record<string, { label: string; value: any }> = {};
                if (params.fieldMappings.metadata) {
                    Object.entries(params.fieldMappings.metadata).forEach(([metaKey, metaConfig]) => {
                        metadata[metaKey] = {
                            label: `${metadataPrefix}-${metaKey}`,
                            value: asset[metaConfig] || "",
                        };
                    });
                }
                return {
                    label: (asset[params.fieldMappings.label] as string) || "",
                    value: (asset[params.fieldMappings.value] as string) || "",
                    metadata,
                };
            }).sort((a, b) => a.label.localeCompare(b.label));

            // Merge into all existing docs
            for (const existingDoc of existingDocs) {
                const existingOptions = existingDoc.options || [];
                // Merge
                const mergedOptions = [...existingOptions];
                newOptions.forEach(newOpt => {
                    const existingIndex = mergedOptions.findIndex(opt => opt.value === newOpt.value);
                    if (existingIndex >= 0) {
                        // Update existing
                        mergedOptions[existingIndex] = {
                            ...mergedOptions[existingIndex],
                            label: newOpt.label,
                            metadata: { ...mergedOptions[existingIndex].metadata, ...newOpt.metadata }
                        };
                    } else {
                        // Add new
                        mergedOptions.push(newOpt);
                    }
                });
                // Sort merged options
                mergedOptions.sort((a, b) => a.label.localeCompare(b.label));
                // Update doc
                await updateDoc(doc(db, collectionName, existingDoc.id), {
                    options: mergedOptions,
                    updatedAt: serverTimestamp()
                });
            }
            setNotification({ type: "success", message: `Flex Options sets for ${params.assetType} merged!` });
            setTimeout(() => setNotification(null), 3000);
            // Clear states
            setExistingDocs([]);
            setActionRequired(false);
            setPendingParams(null);
        } catch (err: any) {
            setNotification({ type: "error", message: err.message || "Failed to merge options sets." });
            setTimeout(() => setNotification(null), 3000);
        } finally {
            setLoading(false);
        }
    };

    const createNewSet = async () => {
        if (!pendingParams) return;
        setLoading(true);
        setNotification(null);
        try {
            const params = pendingParams;
            const collectionName = params.collectionName || "flexFormSelectOptions";
            const metadataPrefix = params.metadataPrefix || "item";

            const uniqueName = `${params.assetType} - ${new Date().toISOString().split('T')[0]}`;
            // Generate options
            const options = params.assetList.map((asset) => {
                const metadata: Record<string, { label: string; value: any }> = {};
                if (params.fieldMappings.metadata) {
                    Object.entries(params.fieldMappings.metadata).forEach(([metaKey, metaConfig]) => {
                        metadata[metaKey] = {
                            label: `${metadataPrefix}-${metaKey}`,
                            value: asset[metaConfig] || "",
                        };
                    });
                }
                return {
                    label: (asset[params.fieldMappings.label] as string) || "",
                    value: (asset[params.fieldMappings.value] as string) || "",
                    metadata,
                };
            }).sort((a, b) => a.label.localeCompare(b.label));

            const docData = {
                name: uniqueName,
                createdAt: serverTimestamp(),
                options,
                updatedAt: serverTimestamp(),
                companyId: params.companyId,
                linkedAssetType: params.assetType,
            };
            await addDoc(collection(db, collectionName), docData);
            setNotification({ type: "success", message: `New Flex Options set for ${uniqueName} created!` });
            setTimeout(() => setNotification(null), 3000);
            // Clear states
            setExistingDocs([]);
            setActionRequired(false);
            setPendingParams(null);
        } catch (err: any) {
            setNotification({ type: "error", message: err.message || "Failed to create new options set." });
            setTimeout(() => setNotification(null), 3000);
        } finally {
            setLoading(false);
        }
    };

    const clearNotification = () => setNotification(null);

    return [
      createFlexOptionsSet,
      { loading, notification, clearNotification, actionRequired, existingDocs, mergeOptions, createNewSet }
    ];
}
